import React, { memo } from 'react'
import { t } from '@lingui/core/macro'
import { useChannelActionContext, useChannelStateContext } from 'stream-chat-react'
import { Icon, Text, View } from '@/components'
import { Getstream } from '@/services'
import { appStatusStore } from '@/stores'
import {
  buildQuotedPreviewA11yLabel,
  ChatLocalMesage,
  getQuotedImageAlbumLabel,
  getQuotedPreview,
  isMessageEdited,
  QUOTED_PREVIEW_ICONS,
  QuotedPreviewModel,
  quotedMessageMemoEquality,
  resolveQuotedForDisplay,
} from '@packages/chat'
import { useIsMessageDeleted } from '@packages/chat/src/stores'
import type { ChatMessageStyles } from '../styles'
import { QuotedTextWithLinks } from '../../QuotedTextWithLinks'

type ChatLocalMessage = ChatLocalMesage

type ResolvedPreview = Exclude<QuotedPreviewModel, { kind: 'deleted' }>

type MentionedUser = { id?: string; name?: string }

type ContentProps = {
  preview: ResolvedPreview
  styles: ChatMessageStyles
  mentionedUsers?: MentionedUser[]
}

const QuotedPreviewContent = ({ preview, styles, mentionedUsers }: ContentProps) => {
  const iconName = QUOTED_PREVIEW_ICONS[preview.kind as keyof typeof QUOTED_PREVIEW_ICONS]

  if (preview.kind === 'image') {
    const label = preview.count > 1
      ? getQuotedImageAlbumLabel(preview.count)
      : (preview.caption ?? t`Photo`)
    return (
      <View style={styles.quotedPreviewRow}>
        <Icon name='camera-01' style={styles.quotedPreviewIcon} />
        <QuotedTextWithLinks
          text={label}
          textCss={[styles.quotedPreviewText, styles.quotedPreviewRowText]}
          linkCss={styles.quotedPreviewLinkText}
          mentionCss={styles.quotedPreviewMentionText}
          mentionedUsers={mentionedUsers}
          numberOfLines={1}
        />
      </View>
    )
  }

  if (preview.kind === 'gif') {
    return (
      <View style={styles.quotedPreviewRow}>
        <Icon name='sticker-circle' style={styles.quotedPreviewIcon} />
        <QuotedTextWithLinks
          text={preview.caption ?? t`GIF`}
          textCss={[styles.quotedPreviewText, styles.quotedPreviewRowText]}
          linkCss={styles.quotedPreviewLinkText}
          mentionCss={styles.quotedPreviewMentionText}
          mentionedUsers={mentionedUsers}
          numberOfLines={1}
        />
      </View>
    )
  }

  if (preview.kind === 'text') {
    return (
      <QuotedTextWithLinks
        text={preview.text}
        textCss={styles.quotedPreviewText}
        linkCss={styles.quotedPreviewLinkText}
        mentionCss={styles.quotedPreviewMentionText}
        mentionedUsers={mentionedUsers}
        numberOfLines={2}
      />
    )
  }

  const label =
    preview.kind === 'poll'
      ? preview.title
      : preview.kind === 'voice'
        ? t`Voice message (${preview.durationLabel})`
        : preview.filename

  return (
    <View style={styles.quotedPreviewRow}>
      {iconName ? <Icon name={iconName as any} style={styles.quotedPreviewIcon} /> : null}
      <Text
        style={[styles.quotedPreviewText, styles.quotedPreviewRowText]}
        text={label}
      />
    </View>
  )
}

// Stream-hosted attachment URLs — bypass the Medias resolver (per
// packages/chat AGENTS.md) by rendering a plain <img>.
const QuotedPreviewThumbnail = ({ preview, styles }: ContentProps) => {
  if (preview.kind !== 'image' && preview.kind !== 'gif') return null
  if (!preview.thumbnailUrl) return null
  return <img src={preview.thumbnailUrl} alt='' css={styles.quotedPreviewThumbnail} />
}

type InnerProps = {
  quotedMessage: ChatLocalMessage
  styles: ChatMessageStyles
}

const QuotedMessagePreviewInner = ({ quotedMessage, styles }: InnerProps) => {
  const preview = getQuotedPreview(quotedMessage)
  const { jumpToMessage } = useChannelActionContext()
  const { closeThread } = Getstream.Chat.useThreadNavigation()
  // In thread mode the active list is `threadMessages`; the channel `messages`
  // here covers the channel-scrollable area, so checking the wrong list flips
  // every in-thread tap into the out-of-window path and triggers Stream's
  // `loadChannelAroundMessage` overlay even when the target is on screen.
  const { messages, thread, threadMessages } = useChannelStateContext()
  const activeMessages = thread ? threadMessages : messages

  if (!preview) return null

  // @spec FR-0077/AC-7@v1 — deleted-original preview collapses to a non-interactive label.
  if (preview.kind === 'deleted') {
    const deletedLabel = t`Original message deleted`
    return (
      <View
        style={styles.quotedPreviewWrapper}
        role='text'
        aria-label={deletedLabel}
      >
        <View style={styles.quotedPreviewAccent} />
        <View style={styles.quotedPreviewBody}>
          <Text style={styles.quotedPreviewDeletedText} text={deletedLabel} />
        </View>
      </View>
    )
  }

  // @spec FR-0077/AC-6@v1 — click the preview to scroll to the original.
  const handleClick = async () => {
    if (thread && quotedMessage.id === thread.id) {
      highlightThreadParent()
      return
    }
    const isInLoadedWindow = activeMessages?.some(m => m?.id === quotedMessage.id) ?? false
    if (isInLoadedWindow) {
      highlightMessageRow(quotedMessage.id)
      return
    }
    if (thread) {
      appStatusStore.set('loading')
      closeThread()
      requestAnimationFrame(async () => {
        try {
          await jumpToMessage(quotedMessage.id)
          await highlightAfterRender(quotedMessage.id)
        } finally {
          appStatusStore.set('idle')
        }
      })
      return
    }
    appStatusStore.set('loading')
    try {
      await jumpToMessage(quotedMessage.id)
      await highlightAfterRender(quotedMessage.id)
    } finally {
      appStatusStore.set('idle')
    }
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      aria-label={buildQuotedPreviewA11yLabel(preview)}
      css={[styles.quotedPreviewTouchable]}
    >
      <View style={styles.quotedPreviewWrapper}>
        <View style={styles.quotedPreviewAccent} />
        <View style={styles.quotedPreviewBody}>
          <Text
            style={styles.quotedPreviewSender}
            text={preview.isCurrentUser ? t`You` : preview.senderName}
          />
          <QuotedPreviewContent
            preview={preview}
            styles={styles}
            mentionedUsers={quotedMessage?.mentioned_users as MentionedUser[] | undefined}
          />
          {isMessageEdited(quotedMessage) && (
            <Text style={styles.quotedPreviewEditedText} text={t`Edited`} />
          )}
        </View>
        <QuotedPreviewThumbnail preview={preview} styles={styles} />
      </View>
    </button>
  )
}

// @spec FR-0077/AC-6@v1 — tag the target row with `.message-highlighted` after the jump.
const HIGHLIGHT_DURATION_MS = 2000

// Wait for the channel to actually paint the target row before tagging it.
// Stream's `jumpToMessage` resolves once the network round trip finishes — at
// that point the React tree has been dispatched but not yet rendered, so a
// synchronous `querySelector` after the await misses the row. Two RAFs cover
// React's commit + Stream's layout effects; once the row is in the DOM, wait
// for `scrollHeight` to stop growing so a smooth scroll lands on the final
// list size — kicking off `scrollIntoView` while `loadMoreFinished` is still
// debouncing in new messages snaps the page to a stale target position
// instead of easing.
async function highlightAfterRender(messageId: string) {
  await new Promise<void>(r => requestAnimationFrame(() => requestAnimationFrame(() => r())))
  await waitForListHeightStable()
  highlightMessageRow(messageId)
}

const SCROLL_HEIGHT_STABLE_TICKS = 2
const SCROLL_HEIGHT_TICK_MS = 30
const SCROLL_HEIGHT_MAX_WAIT_MS = 500

function waitForListHeightStable(): Promise<void> {
  return new Promise(resolve => {
    const list = document.querySelector<HTMLElement>('.str-chat__list')
    if (!list) {
      resolve()
      return
    }
    const start = performance.now()
    let lastHeight = list.scrollHeight
    let stableTicks = 0
    const tick = () => {
      const h = list.scrollHeight
      if (h === lastHeight) {
        stableTicks += 1
        if (stableTicks >= SCROLL_HEIGHT_STABLE_TICKS) {
          resolve()
          return
        }
      } else {
        stableTicks = 0
        lastHeight = h
      }
      if (performance.now() - start > SCROLL_HEIGHT_MAX_WAIT_MS) {
        resolve()
        return
      }
      window.setTimeout(tick, SCROLL_HEIGHT_TICK_MS)
    }
    window.setTimeout(tick, SCROLL_HEIGHT_TICK_MS)
  })
}

function highlightMessageRow(messageId: string) {
  const row = document.querySelector<HTMLElement>(
    `li.str-chat__li[data-message-id="${CSS.escape(messageId)}"]`,
  )
  if (!row) return
  // Bring the row into view when it isn't already. Stream's MessageList runs
  // its own `useLayoutEffect` to scroll the highlighted row to center, but
  // when the jump triggers a `loadChannelAroundMessage` the new `messages`
  // are dispatched on a debounce that resolves AFTER Stream sets
  // `highlightedMessageId` — so by the time React commits, the target row
  // hasn't been rendered yet and Stream's `querySelector` returns null. The
  // effect doesn't re-fire when the row finally mounts because its dep
  // (`highlightedMessageId`) hasn't changed. Doing the scroll ourselves once
  // the row is in the DOM (via `highlightAfterRender`) closes that gap; we
  // skip the scroll when the row is already visible so the in-window path
  // (Bug 1) doesn't jiggle.
  scrollRowIntoViewIfNeeded(row)
  row.classList.remove('message-highlighted')
  // Reading offsetWidth forces a synchronous reflow so the keyframe restarts
  // when the class is re-added — otherwise the same animation token replays
  // as a no-op for the second-and-onwards click on the same row.
  void row.offsetWidth
  row.classList.add('message-highlighted')
  window.setTimeout(() => {
    row.classList.remove('message-highlighted')
  }, HIGHLIGHT_DURATION_MS)
}

function scrollRowIntoViewIfNeeded(row: HTMLElement) {
  const list = row.closest<HTMLElement>('.str-chat__list')
  if (!list) {
    row.scrollIntoView({ block: 'center', behavior: 'smooth' })
    return
  }
  const rowRect = row.getBoundingClientRect()
  const listRect = list.getBoundingClientRect()
  const isFullyVisible = rowRect.top >= listRect.top && rowRect.bottom <= listRect.bottom
  if (isFullyVisible) return
  row.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

// @spec FR-0077/AC-6@v1 — reply-quoting-thread-parent scrolls/highlights the ThreadHead.
function highlightThreadParent() {
  const parent = document.querySelector<HTMLElement>('.str-chat__parent-message-li')
  if (!parent) return
  parent.scrollIntoView({ block: 'center', behavior: 'smooth' })
  parent.classList.remove('message-highlighted')
  void parent.offsetWidth
  parent.classList.add('message-highlighted')
  window.setTimeout(() => {
    parent.classList.remove('message-highlighted')
  }, HIGHLIGHT_DURATION_MS)
}

const MemoizedQuotedPreview = memo(
  QuotedMessagePreviewInner,
  (prev, next) =>
    quotedMessageMemoEquality(prev.quotedMessage, next.quotedMessage)
    && prev.styles === next.styles,
)

type QuotedMessagePreviewProps = {
  message: ChatLocalMessage
  styles: ChatMessageStyles
}

// @spec FR-0077/AC-5@v1 — Quick Reply preview slot rendered above the reply.
// @spec FR-0077/AC-7@v1 — flip to deleted-original state via deletedMessageIds store.
export const QuotedMessagePreview = ({ message, styles }: QuotedMessagePreviewProps) => {
  const { messages } = useChannelStateContext()
  const quotedSnapshot = message?.quoted_message as ChatLocalMessage | undefined
  const wasDeletedThisSession = useIsMessageDeleted(quotedSnapshot?.id)

  if (!quotedSnapshot) return null

  const liveQuoted = messages?.find(m => m.id === quotedSnapshot.id) as ChatLocalMessage | undefined
  const quotedMessage = resolveQuotedForDisplay(quotedSnapshot, liveQuoted, wasDeletedThisSession)
  if (!quotedMessage) return null

  return <MemoizedQuotedPreview quotedMessage={quotedMessage} styles={styles} />
}
